// Универсальная форма для add/view/edit транзакции. Минимально оформлено —
// дизайн будет добавлен позже отдельным проходом по styles.css.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { Category, Transaction } from '../api/types';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { SheetCta } from './SheetCta';
import { useBackButton, useMainButton } from '../lib/use-main-button';

type Mode = 'add' | 'edit';

type Props = {
  mode: Mode;
  initial?: Transaction | null; // обязателен для mode=edit
  initialKind?: 'expense' | 'income'; // pre-select для add (если не задан — expense)
  onClose: () => void;
};

type Kind = 'expense' | 'income';
type Currency = 'UZS' | 'USD' | 'EUR' | 'RUB';

// "30 000" / "30000" / "30000.50" → bigint (тийины). null если невалидно.
function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

function minorToMajor(minorStr: string): string {
  const major = BigInt(minorStr) / 100n;
  return major.toString();
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

export function TransactionModal({
  mode,
  initial,
  initialKind,
  onClose,
}: Props): JSX.Element {
  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [kind, setKind] = useState<Kind>(initial?.kind ?? initialKind ?? 'expense');
  const [amount, setAmount] = useState<string>(
    initial ? minorToMajor(initial.amount_minor) : '',
  );
  const [currency, setCurrency] = useState<Currency>(
    (initial?.currency as Currency) ?? 'UZS',
  );
  const [categorySlug, setCategorySlug] = useState<string>(initial?.category_slug ?? '');
  const [note, setNote] = useState<string>(initial?.note ?? '');
  const [date, setDate] = useState<string>(
    initial?.occurred_at ? initial.occurred_at.slice(0, 10) : todayIsoDate(),
  );

  const categoriesQ = useQuery({
    queryKey: ['categories', kind],
    queryFn: () => api.categories(kind, false),
  });

  const queryClient = useQueryClient();
  const toast = useToast();

  const createMut = useMutation({
    mutationFn: () => {
      const minor = parseAmountToMinor(amount);
      if (!minor) throw new Error('Invalid amount');
      return api.createTransaction({
        kind,
        amount_minor: minor.toString(),
        currency,
        category_slug: categorySlug || null,
        note: note || null,
        occurred_at: new Date(`${date}T12:00:00.000Z`).toISOString(),
      });
    },
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось записать операцию')),
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!initial) throw new Error('No initial transaction');
      const minor = parseAmountToMinor(amount);
      if (!minor) throw new Error('Invalid amount');
      return api.updateTransaction(initial.id, {
        kind,
        amount_minor: minor.toString(),
        currency,
        category_slug: categorySlug || null,
        note: note || null,
        occurred_at: new Date(`${date}T12:00:00.000Z`).toISOString(),
      });
    },
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить изменения')),
  });

  const isPending = createMut.isPending || updateMut.isPending;
  const minor = parseAmountToMinor(amount);
  const isValid = minor !== null && minor > 0n;

  const submit = (): void => {
    if (!isValid) return;
    if (mode === 'add') createMut.mutate();
    else updateMut.mutate();
  };

  const cta = useMainButton({
    text: mode === 'add' ? 'Добавить' : 'Сохранить',
    onClick: submit,
    enabled: isValid && !isPending,
    progress: isPending,
  });

  // Если выбрана категория, которой нет в текущем kind (после переключения kind) — сбросить.
  const categoryStillValid = useMemo(() => {
    if (!categorySlug) return true;
    if (!categoriesQ.data) return true;
    return categoriesQ.data.some((c) => c.slug === categorySlug);
  }, [categorySlug, categoriesQ.data]);

  useEffect(() => {
    if (!categoryStillValid) setCategorySlug('');
  }, [categoryStillValid]);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">
          {mode === 'add' ? 'Новая транзакция' : 'Редактировать транзакцию'}
        </div>

        <div className="kind-toggle">
          <button
            type="button"
            className={`kind-btn ${kind === 'expense' ? 'active' : ''}`}
            onClick={() => setKind('expense')}
          >
            Расход
          </button>
          <button
            type="button"
            className={`kind-btn ${kind === 'income' ? 'active' : ''}`}
            onClick={() => setKind('income')}
          >
            Доход
          </button>
        </div>

        <label className="form-field">
          <span className="form-label">Сумма</span>
          <div className="amount-row">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`form-input ${!isValid && amount ? 'invalid' : ''}`}
              autoFocus
            />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="form-select currency-select"
            >
              <option value="UZS">UZS</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="RUB">RUB</option>
            </select>
          </div>
          {!isValid && amount ? (
            <span className="form-hint error">Введи положительное число</span>
          ) : null}
        </label>

        <label className="form-field">
          <span className="form-label">Категория</span>
          <select
            value={categorySlug}
            onChange={(e) => setCategorySlug(e.target.value)}
            className="form-select"
          >
            <option value="">Без категории</option>
            {(categoriesQ.data ?? []).map((c: Category) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-label">Описание</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="form-input form-textarea"
            rows={2}
            placeholder="опционально"
          />
        </label>

        <label className="form-field">
          <span className="form-label">Дата</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form-input"
          />
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isPending}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
